// Dedicated merchant storefront page — every role can land here, reachable
// from the customer/recipient cart-builder. Shows the verified business, its
// pinned location, delivery ETA, care packages, and full browsable inventory,
// with a one-tap "start a cart from this store" that preselects the merchant.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Loader2, MapPin, Star, ShoppingBag, Plus,
  ShieldCheck, Calendar, Clock, Home, Gift, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill, EmptyState } from '../components/shared/primitives';
import { MapView } from '../components/shared/MapView';
import { CUSTOMER_NAV, RECIPIENT_NAV, ADMIN_NAV, MERCHANT_NAV } from './_navs';
import { storefront, merchantItems, carePackages, type MerchantItem } from '../lib/api';
import { iconFor } from '../lib/icons';
import { formatHtgMinor, formatUsdFromHtgMinor, deliveryEtaLabel } from '../lib/money';

const NAV_BY_ROLE: Record<string, NavSection[]> = {
  customer: CUSTOMER_NAV, recipient: RECIPIENT_NAV, merchant: MERCHANT_NAV, admin: ADMIN_NAV, super_admin: ADMIN_NAV,
};

export function MerchantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const { user } = useAuth();
  const role = user?.role ?? 'customer';
  const nav = NAV_BY_ROLE[role] ?? CUSTOMER_NAV;
  const isShopper = role === 'customer' || role === 'recipient';

  const q = useQuery({ queryKey: ['storefront', 'merchant', userId], queryFn: () => storefront.merchant(userId), enabled: !isNaN(userId) });
  const pkgQ = useQuery({ queryKey: ['storefront', 'packages'], queryFn: carePackages.list, enabled: isShopper });

  const m = q.data?.merchant;
  const eta = deliveryEtaLabel(m?.avg_fulfillment_hours);
  const myPackages = useMemo(() => (pkgQ.data ?? []).filter((p) => p.merchant?.user_id === userId), [pkgQ.data, userId]);

  const cartHref = role === 'recipient' ? `/recipient/request/new?merchant=${userId}` : `/customer/requests/new?merchant=${userId}`;
  const backHref = role === 'admin' || role === 'super_admin' ? '/admin/merchants' : role === 'recipient' ? '/recipient' : '/customer';
  const backLabel = role === 'admin' || role === 'super_admin' ? 'All merchants' : role === 'recipient' ? 'Home' : 'Dashboard';

  return (
    <PortalShell
      nav={nav}
      activeId={role === 'admin' || role === 'super_admin' ? 'merchants' : isShopper ? (role === 'recipient' ? 'home' : 'dashboard') : 'dashboard'}
      title={m?.business_name ?? 'Merchant'}
      subtitle={m?.city?.name ?? undefined}
      actions={isShopper && m ? (
        <Link to={cartHref} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12px] font-semibold hover:bg-brand-700">
          <Plus className="w-3.5 h-3.5" /> Start a cart
        </Link>
      ) : undefined}
    >
      <Link to={backHref} className="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-secondary-2)] hover:text-[var(--color-ink)] mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
      </Link>

      {q.isLoading && <Card><div className="text-center py-12 text-[13px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading merchant…</div></Card>}
      {q.error && <Card><p className="text-[13px] text-[var(--color-danger-fg)]">Couldn't load this merchant — they may be unapproved or in a city not yet active.</p></Card>}

      {q.data && m && (
        <div className="space-y-5">
          {/* Header */}
          <Card>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4 min-w-0">
                <span className="inline-flex w-14 h-14 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shrink-0"><ShoppingBag className="w-6 h-6" /></span>
                <div className="min-w-0">
                  <h2 className="text-[22px] font-semibold tracking-tight text-[var(--color-ink)] truncate">{m.business_name}</h2>
                  {m.legal_name && <p className="text-[12px] text-[var(--color-secondary-2)]">{m.legal_name}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--color-secondary)]">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[var(--color-muted)]" /> {m.city?.name ?? '—'}</span>
                    {m.address_line && <span className="inline-flex items-center gap-1"><Home className="w-3.5 h-3.5 text-[var(--color-muted)]" /> {m.address_line}</span>}
                    {m.rating && <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-[var(--color-warning-fg)]" /> {Number(m.rating).toFixed(1)}</span>}
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Joined {new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[var(--color-success-fg)]" /> KYB verified</p>
                <p className="mt-1"><StatusPill status={m.status} /></p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Items in stock" value={String(q.data.item_count)} />
              <Stat label="Categories" value={String(q.data.categories.length)} />
              <Stat label="Care packages" value={String(myPackages.length)} />
              <Stat label="Delivery" value={eta?.label ?? '—'} />
            </div>
          </Card>

          {/* Location map */}
          {m.latitude && m.longitude && (
            <Card padding="p-0" className="overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[var(--color-hairline)]">
                <p className="text-[12px] font-semibold text-[var(--color-body)] inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Store location</p>
              </div>
              <MapView height="260px" zoom={15} markers={[{ lat: Number(m.latitude), lng: Number(m.longitude), label: m.business_name, color: '#1455E8' }]} />
            </Card>
          )}

          {/* Care packages from this merchant */}
          {isShopper && myPackages.length > 0 && (
            <Card>
              <SectionHeader title="Care packages" subtitle="One-tap boxes from this store — exact prices." />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {myPackages.map((p) => (
                  <Link key={p.id} to={`/packages/${p.slug}`} className="rounded-xl border border-[var(--color-border)] p-3.5 hover:border-brand-300 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)] shrink-0"><Gift className="w-4.5 h-4.5" /></span>
                      <div className="min-w-0">
                        <p className="text-[13.5px] font-semibold text-[var(--color-ink)] truncate">{p.name}</p>
                        {p.subtitle && <p className="text-[11px] text-[var(--color-secondary-2)] truncate">{p.subtitle}</p>}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-[12px] font-mono font-semibold text-[var(--color-ink)]">{p.approx_htg_minor ? formatHtgMinor(p.approx_htg_minor) : '—'}</span>
                      <span className="text-[11px] text-[var(--color-muted)]">{p.items_count} items</span>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {/* Full inventory */}
          <InventorySection userId={userId} categories={q.data.categories} cartHref={cartHref} showCta={isShopper} />
        </div>
      )}
    </PortalShell>
  );
}

function InventorySection({
  userId, categories, cartHref, showCta,
}: {
  userId: number;
  categories: Array<{ slug: string; label: string; count: number }>;
  cartHref: string;
  showCta: boolean;
}) {
  const [tab, setTab] = useState<string>('all');
  const itemsQ = useQuery({
    queryKey: ['merchant', userId, 'items', tab],
    queryFn: () => merchantItems.browse(userId, tab === 'all' ? undefined : { category: tab }),
    enabled: !isNaN(userId),
  });

  return (
    <Card>
      <SectionHeader
        title="Inventory"
        subtitle="Everything in stock right now."
        actions={showCta ? <Link to={cartHref} className="text-[12px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-1">Start a cart <ArrowRight className="w-3 h-3" /></Link> : undefined}
      />
      {/* Category tabs */}
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        <Tab active={tab === 'all'} onClick={() => setTab('all')} label="All" />
        {categories.map((c) => <Tab key={c.slug} active={tab === c.slug} onClick={() => setTab(c.slug)} label={`${c.label} (${c.count})`} />)}
      </div>

      {itemsQ.isLoading ? (
        <div className="text-center py-10 text-[13px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
      ) : (itemsQ.data ?? []).length === 0 ? (
        <EmptyState icon={ShoppingBag} title="Nothing here" body="No items in this category right now." />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {(itemsQ.data ?? []).map((it) => <ItemCard key={it.id} item={it} />)}
        </div>
      )}
    </Card>
  );
}

function Tab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} className={`px-3 py-1 rounded-full text-[12px] font-semibold transition-colors ${active ? 'bg-[var(--color-ink)] text-white' : 'bg-[var(--color-panel)] text-[var(--color-body)] hover:bg-[var(--color-border)]'}`}>{label}</button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-[var(--color-hairline)] rounded-lg p-3">
      <p className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">{label}</p>
      <p className="mt-1 text-[16px] font-semibold tracking-tight text-[var(--color-ink)] truncate inline-flex items-center gap-1">{label === 'Delivery' && <Clock className="w-3.5 h-3.5 text-[var(--color-muted)]" />}{value}</p>
    </div>
  );
}

function ItemCard({ item }: { item: MerchantItem }) {
  const Icon = iconFor(item.system_icon);
  return (
    <div className="bg-white border border-[var(--color-hairline)] rounded-xl p-3 flex flex-col">
      <div className="aspect-square rounded-lg bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/40 flex items-center justify-center text-brand-600 mb-3"><Icon className="w-8 h-8" /></div>
      <p className="text-[12.5px] font-semibold text-[var(--color-ink)] leading-tight line-clamp-2">{item.name}</p>
      <p className="text-[10.5px] text-[var(--color-secondary-2)] mt-0.5">{item.unit}</p>
      <div className="mt-auto pt-2 flex items-baseline justify-between">
        <span className="text-[13px] font-mono font-semibold text-[var(--color-ink)]">{formatHtgMinor(item.price_htg_minor)}</span>
        <span className="text-[10px] text-[var(--color-muted)] font-mono">≈ {formatUsdFromHtgMinor(item.price_htg_minor)}</span>
      </div>
    </div>
  );
}
